<script lang="ts" setup>
  import { useTemplateRef, watch, onMounted, onBeforeUnmount, onUnmounted, ref, computed, type Ref, nextTick } from 'vue';
  import { useTranslator } from '@othercommerce/foundation';
  import { NotFoundException } from '@zxing/library';
  import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';

  // TODO: This component should be moved somewhere else, as it is universal and not specific to storefront.

  interface Events {
    (e: 'scanned', value: string): void,
  }

  const emit = defineEmits<Events>();
  const open = defineModel('open', { type: Boolean, required: false, default: false });
  const translator = useTranslator();
  const modal = useTemplateRef('modal');
  const video = useTemplateRef('video');

  const reader = ref() as Ref<BrowserMultiFormatReader | undefined>;
  const scanner = ref() as Ref<IScannerControls | undefined>;
  const scanned = ref() as Ref<string | undefined>;
  const error = ref() as Ref<string | undefined>;

  const classes = computed(() => {
    return {
      'scanned': !!scanned.value,
    };
  });

  function show() {
    showModal();
    start();
  }

  function hide() {
    stop();
    hideModal();
  }

  function showModal() {
    if (modal.value) {
      modal.value.showModal();
    }
  }

  function hideModal() {
    if (modal.value) {
      modal.value.close();
    }
  }

  function start() {
    scanned.value = undefined;
    reader.value = new BrowserMultiFormatReader();

    if (video.value) {
      reader.value.decodeFromVideoDevice(undefined, video.value, (result, error) => {
        if (result) {
          scanned.value = result.getText();
        }

        if (error && !(error instanceof NotFoundException)) {
          console.error(error);
        }
      }).then((controls) => {
        scanner.value = controls;
      }).catch((reason) => {
        console.error(reason);
        error.value = translator.trans('APP.ERROR.BARCODE_SCANNER_UNAVAILABLE');
      });
    }
  }

  function stop() {
    scanner.value?.stop();
    scanner.value = undefined;
    reader.value = undefined;
    error.value = undefined;
  }

  function popstateEventListener() {
    hide();
  }

  function submit() {
    let value = scanned.value;

    hide();

    // wait for the modal to close before emitting the scanned value.
    nextTick(() => {
      if (value) {
        emit('scanned', value);
      }
    });
  }

  onMounted(() => {
    if (open.value) {
      show();
    }
  });

  onBeforeUnmount(() => {
    hide();
  });

  onMounted(() => {
    window.addEventListener('popstate', popstateEventListener);
  });

  onUnmounted(() => {
    window.removeEventListener('popstate', popstateEventListener);
  });

  watch(open, (value) => {
    if (value) {
      show();
    } else {
      hide();
    }
  });
</script>
<template>
  <dialog ref="modal" :class="classes" class="barcode-scanner" v-bind="$attrs" @close.stop="open = false" @change.stop="void 0">
    <div class="barcode-scanner-container">
      <video ref="video" playsinline />
      <div class="mask">
        <div class="top" />
        <div class="left" />
        <div class="pointer">
          <div class="corner ul-corner" />
          <div class="corner ur-corner" />
          <div class="corner ll-corner" />
          <div class="corner lr-corner" />
        </div>
        <div class="right" />
        <div class="bottom" />
      </div>
      <div class="instruction">
        {{ $t('APP.MESSAGE.BARCODE_INSTRUCTION') }}
      </div>
      <div v-if="error" class="error">
        {{ error }}
      </div>
      <div class="scanned">
        {{ scanned }}
      </div>
      <div class="actions">
        <button :aria-label="$t('APP.ACTION.CLOSE')" class="cancel" role="button" @click="open = false">
          <svg fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
          </svg>
        </button>
        <button :aria-label="$t('APP.ACTION.CHOOSE')" :disabled="!scanned" class="submit" role="button" @click="submit">
          <svg fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
          </svg>
        </button>
      </div>
    </div>
  </dialog>
</template>
<style lang="scss" scoped>
  .barcode-scanner {
    background: none;
    border: none;
    min-width: 100%;
    min-height: 100dvh;
    cursor: default;
    padding: 0;
    margin: 0;
    top: 0;
    left: 0;

    &.scanned {
      .pointer {
        .corner {
          &:before, &:after {
            background: greenyellow;
          }
        }
      }
    }
  }

  .barcode-scanner-container {
    position: relative;
    display: flex;
    background: #000;
    width: 100%;
    height: 100dvh;
    overflow: hidden;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .mask {
      position: absolute;
      display: grid;
      grid-template-columns: 1fr 80% 1fr;
      grid-template-rows: 1fr auto 1fr;
      inset: 0;

      .top, .bottom, .left, .right {
        background: rgba(0, 0, 0, 0.75);
      }

      .pointer {
        grid-column: 2/3;
        grid-row: 2/3;
      }

      .top {
        grid-column: 1 / 4;
        grid-row: 1 / 2;
      }

      .left {
        grid-column: 1 / 2;
        grid-row: 2 / 3;
      }

      .right {
        grid-column: 3 / 4;
        grid-row: 2 / 3;
      }

      .bottom {
        grid-column: 1 / 4;
        grid-row: 3 / 4;
      }
    }

    .pointer {
      position: relative;
      aspect-ratio: 4 / 2;
      background: transparent;
      animation: scanning 0.5s infinite alternate;
      border: 2px dashed #d3d3d333;
      color: lightgray;

      .corner {
        &:before, &:after {
          content: "";
          position: absolute;
          background: lightgray;
        }
      }

      .ul-corner {
        &:before {
          top: -3px;
          left: -3px;
          width: 32px;
          height: 4px;
        }

        &:after {
          top: -3px;
          left: -3px;
          width: 4px;
          height: 32px;
        }
      }

      .ur-corner {
        &:before {
          top: -3px;
          right: -3px;
          width: 32px;
          height: 4px;
        }

        &:after {
          top: -3px;
          right: -3px;
          width: 4px;
          height: 32px;
        }
      }

      .ll-corner {
        &:before {
          bottom: -3px;
          left: -3px;
          width: 32px;
          height: 4px;
        }

        &:after {
          bottom: -3px;
          left: -3px;
          width: 4px;
          height: 32px;
        }
      }

      .lr-corner {
        &:before {
          bottom: -3px;
          right: -3px;
          width: 32px;
          height: 4px;
        }

        &:after {
          bottom: -3px;
          right: -3px;
          width: 4px;
          height: 32px;
        }
      }
    }

    .scanned {
      font-family: "Courier New", Courier, monospace;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 1rem;
      font-weight: bold;
      text-shadow: 0 0 3px black;
      text-rendering: geometricPrecision;
    }

    .instruction {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 5rem;
      width: 100%;
      color: white;
      font-family: "Arial", sans-serif;
      font-size: 0.75rem;
      text-shadow: 0 0 3px black;
      text-rendering: geometricPrecision;
      text-align: center;
      text-transform: uppercase;
      padding: 2rem;
    }

    .error {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      color: red;
      font-family: "Arial", sans-serif;
      font-size: 0.75rem;
      text-shadow: 0 0 3px black;
      text-rendering: geometricPrecision;
      text-align: center;
      text-transform: uppercase;
      padding: 2rem;
    }

    .actions {
      position: absolute;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      padding: 2rem;
      gap: 1rem;

      button {
        display: flex;
        width: 3rem;
        height: 3rem;
        border-radius: 50%;
        font-size: 2.5rem;
        border: none;
        color: white;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &.cancel {
          background: red;
        }

        &.submit {
          background: green;
        }

        &:disabled {
          opacity: 0.5;
          background: dimgrey;
          cursor: not-allowed;
        }
      }
    }
  }

  @keyframes scanning {
    0% {
      opacity: 0.375;
    }
    100% {
      opacity: 1;
    }
  }
</style>
